import { BulkColumnConfig, ValidatedRow, ValidationError } from '../../BulkImport/types';

const VALID_COUNTRY_CODES = ['+593', '+52', '+506', '+58', '+56', '+57', '+51'];
const VALID_VEHICLE_TYPES = ['BIKE', 'LITE'];
const VALID_CURRENCIES = ['USD', 'MXN', 'CRC', 'VES', 'CLP', 'COP', 'PEN'];
const VALID_LANGUAGES = ['ES', 'EN'];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isEmpty = (value: any): boolean => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') return value.trim() === '';
    return false;
};

const validateDriverRow = (row: Record<string, any>, rowIndex: number): ValidatedRow => {
    const errors: ValidationError[] = [];

    if (isEmpty(row.name)) {
        errors.push({ row: rowIndex, field: 'name', message: 'BULK.VALIDATION.REQUIRED' });
    }

    if (isEmpty(row.lastName)) {
        errors.push({ row: rowIndex, field: 'lastName', message: 'BULK.VALIDATION.REQUIRED' });
    }

    if (isEmpty(row.email)) {
        errors.push({ row: rowIndex, field: 'email', message: 'BULK.VALIDATION.REQUIRED' });
    } else if (!EMAIL_REGEX.test(String(row.email).trim())) {
        errors.push({ row: rowIndex, field: 'email', message: 'BULK.VALIDATION.INVALID_EMAIL' });
    }

    if (isEmpty(row.mobile)) {
        errors.push({ row: rowIndex, field: 'mobile', message: 'BULK.VALIDATION.REQUIRED' });
    }

    if (isEmpty(row.vehicleModelName)) {
        errors.push({ row: rowIndex, field: 'vehicleModelName', message: 'BULK.VALIDATION.REQUIRED' });
    }

    if (isEmpty(row.MCNumber)) {
        errors.push({ row: rowIndex, field: 'MCNumber', message: 'BULK.VALIDATION.REQUIRED' });
    }

    if (isEmpty(row.vehicleColor)) {
        errors.push({ row: rowIndex, field: 'vehicleColor', message: 'BULK.VALIDATION.REQUIRED' });
    }

    if (isEmpty(row.vehicleType)) {
        errors.push({ row: rowIndex, field: 'vehicleType', message: 'BULK.VALIDATION.REQUIRED' });
    } else if (!VALID_VEHICLE_TYPES.includes(String(row.vehicleType).toUpperCase())) {
        errors.push({ row: rowIndex, field: 'vehicleType', message: 'BULK.VALIDATION.INVALID_VEHICLE_TYPE' });
    }

    if (isEmpty(row.vehicleYear)) {
        errors.push({ row: rowIndex, field: 'vehicleYear', message: 'BULK.VALIDATION.REQUIRED' });
    } else {
        const year = Number(row.vehicleYear);
        if (Number.isNaN(year) || year < 1900 || year > new Date().getFullYear() + 1) {
            errors.push({ row: rowIndex, field: 'vehicleYear', message: 'BULK.VALIDATION.INVALID_YEAR' });
        }
    }

    if (!isEmpty(row.countryCode) && !VALID_COUNTRY_CODES.includes(String(row.countryCode).trim())) {
        errors.push({ row: rowIndex, field: 'countryCode', message: 'BULK.VALIDATION.INVALID_COUNTRY_CODE' });
    }

    if (!isEmpty(row.currency) && !VALID_CURRENCIES.includes(String(row.currency).toUpperCase())) {
        errors.push({ row: rowIndex, field: 'currency', message: 'BULK.VALIDATION.INVALID_CURRENCY' });
    }

    if (!isEmpty(row.language) && !VALID_LANGUAGES.includes(String(row.language).toUpperCase())) {
        errors.push({ row: rowIndex, field: 'language', message: 'BULK.VALIDATION.INVALID_LANGUAGE' });
    }

    return {
        name: String(row.name || '').trim(),
        lastName: String(row.lastName || '').trim(),
        email: String(row.email || '').trim().toLowerCase(),
        mobile: String(row.mobile || '').trim(),
        countryCode: String(row.countryCode || '+593').trim(),
        language: String(row.language || 'ES').toUpperCase().trim(),
        currency: String(row.currency || 'USD').toUpperCase().trim(),
        vehicleModelName: String(row.vehicleModelName || '').trim(),
        MCNumber: String(row.MCNumber || '').trim(),
        vehicleColor: String(row.vehicleColor || '').trim(),
        vehicleType: String(row.vehicleType || '').toUpperCase().trim(),
        vehicleYear: Number(row.vehicleYear) || 0,
        _rowIndex: rowIndex,
        _errors: errors,
        _isValid: errors.length === 0,
    };
};

export const validateDrivers = (rows: Record<string, any>[]): ValidatedRow[] => {
    const validated = rows.map((row, index) => validateDriverRow(row, index + 1));

    const emailCounts: Record<string, number[]> = {};
    validated.forEach((driver) => {
        if (driver.email) {
            const email = driver.email.toLowerCase();
            if (!emailCounts[email]) emailCounts[email] = [];
            emailCounts[email].push(driver._rowIndex);
        }
    });

    Object.entries(emailCounts).forEach(([, rowIndices]) => {
        if (rowIndices.length > 1) {
            rowIndices.forEach((rowIndex) => {
                const driver = validated.find((d) => d._rowIndex === rowIndex);
                if (driver) {
                    driver._errors.push({
                        row: rowIndex,
                        field: 'email',
                        message: 'BULK.VALIDATION.DUPLICATE_EMAIL',
                    });
                    driver._isValid = false;
                }
            });
        }
    });

    return validated;
};

export const DRIVER_COLUMNS: BulkColumnConfig[] = [
    {
        key: 'name', label: 'Nombre', required: true, example: 'Juan', width: 120,
    },
    {
        key: 'lastName', label: 'Apellido', required: true, example: 'Pérez', width: 120,
    },
    {
        key: 'email', label: 'Correo electrónico', required: true, example: 'juan@email.com', width: 200,
    },
    {
        key: 'mobile', label: 'Teléfono', required: true, example: '0991234567', width: 130,
    },
    {
        key: 'countryCode', label: 'Código de país', required: false, example: '+593', width: 100,
    },
    {
        key: 'language', label: 'Idioma (ES/EN)', required: false, example: 'ES', width: 80,
    },
    {
        key: 'currency', label: 'Moneda (USD/MXN/COP/PEN)', required: false, example: 'USD', width: 80,
    },
    {
        key: 'vehicleModelName', label: 'Marca del vehículo', required: true, example: 'Honda', width: 130,
    },
    {
        key: 'MCNumber', label: 'Placa del vehículo', required: true, example: 'ABC-1234', width: 110,
    },
    {
        key: 'vehicleColor', label: 'Color del vehículo', required: true, example: 'Rojo', width: 110,
    },
    {
        key: 'vehicleType', label: 'Tipo de vehículo (BIKE/LITE)', required: true, example: 'BIKE', width: 110,
    },
    {
        key: 'vehicleYear', label: 'Año del vehículo', required: true, example: '2022', width: 100,
    },
];
